import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Header from "@/components/layout/Header";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import MerchantDashboard from "@/pages/MerchantDashboard";
import CustomerDashboard from "@/pages/CustomerDashboard";
import CreatePaymentLink from "@/pages/CreatePaymentLink";
import PaymentLinks from "@/pages/PaymentLinks";
import PublicPayPage from "@/pages/PublicPayPage";
import SendMoney from "@/pages/SendMoney";
import Wallet from "@/pages/Wallet";
import Transactions from "@/pages/Transactions";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import ProfileSettings from "@/pages/ProfileSettings";
import Withdrawals from "@/pages/Withdrawals";
import Analytics from "@/pages/Analytics";
import Subscriptions from "@/pages/Subscriptions";
import PaymentLinksFeature from "@/pages/atelier/PaymentLinksFeature";
import P2PFeature from "@/pages/atelier/P2PFeature";
import SecurityFeature from "@/pages/atelier/SecurityFeature";
import ConciergeFeature from "@/pages/patrons/ConciergeFeature";
import MultiCurrencyFeature from "@/pages/patrons/MultiCurrencyFeature";
import InstantTransferFeature from "@/pages/patrons/InstantTransferFeature";
import VaultedSessionsFeature from "@/pages/patrons/VaultedSessionsFeature";

function Shell({ children }) {
  const location = useLocation();
  const hideHeader = ["/login", "/register", "/auth/callback", "/forgot-password", "/reset-password"].some((p) => location.pathname.startsWith(p))
    || location.pathname.startsWith("/pay/")
    || location.pathname.startsWith("/payment/");
  return (
    <div className="App min-h-screen">
      {!hideHeader && <Header />}
      {children}
    </div>
  );
}

function HomeRedirect() {
  // Landing page always renders the public/anonymous view.
  // If a user happens to be authenticated, sign them out automatically.
  const { user, logout } = useAuth();
  React.useEffect(() => {
    if (user) {
      logout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);
  return <Landing />;
}

function AppRouter() {
  const location = useLocation();
  // Intercept Emergent OAuth callback BEFORE other routes
  if (location.hash?.includes("session_id=") && !location.pathname.startsWith("/auth/callback")) {
    return <AuthCallback />;
  }

  return (
    <Shell>
      <Routes>
        <Route path="/" element={<HomeRedirect />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        <Route path="/atelier/payment-links" element={<PaymentLinksFeature />} />
        <Route path="/atelier/p2p-transfers" element={<P2PFeature />} />
        <Route path="/atelier/security" element={<SecurityFeature />} />

        <Route path="/patrons/concierge-ui" element={<ConciergeFeature />} />
        <Route path="/patrons/multi-currency" element={<MultiCurrencyFeature />} />
        <Route path="/patrons/instant-transfer" element={<InstantTransferFeature />} />
        <Route path="/patrons/vaulted-sessions" element={<VaultedSessionsFeature />} />

        <Route path="/pay/:linkId" element={<PublicPayPage />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />

        <Route path="/merchant" element={<ProtectedRoute role="merchant"><MerchantDashboard /></ProtectedRoute>} />
        <Route path="/merchant/links" element={<ProtectedRoute role="merchant"><PaymentLinks /></ProtectedRoute>} />
        <Route path="/merchant/links/new" element={<ProtectedRoute role="merchant"><CreatePaymentLink /></ProtectedRoute>} />
        <Route path="/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
        <Route path="/withdrawals" element={<ProtectedRoute role="merchant"><Withdrawals /></ProtectedRoute>} />

        <Route path="/customer" element={<ProtectedRoute role="customer"><CustomerDashboard /></ProtectedRoute>} />
        <Route path="/send" element={<ProtectedRoute><SendMoney /></ProtectedRoute>} />
        <Route path="/wallet" element={<ProtectedRoute><Wallet /></ProtectedRoute>} />
        <Route path="/transactions" element={<ProtectedRoute><Transactions /></ProtectedRoute>} />
        <Route path="/subscriptions" element={<ProtectedRoute><Subscriptions /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><ProfileSettings /></ProtectedRoute>} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Shell>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <Toaster theme="dark" position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}
